import React from 'react';
import { SiteConditions } from '../../types';
import { useIdioma } from '../../i18n/idioma';
import type { Clave } from '../../i18n/textos';
import { AlertTriangle, Info, MapPin, Ruler, Mountain, Truck, Sun, Snowflake, Thermometer } from 'lucide-react';

/* Los valores que se GUARDAN siguen en inglés —van a la base de datos y a los
   cálculos—, así que lo que se traduce es solo el rótulo del botón. */
const SUELO: Record<NonNullable<SiteConditions['soilType']>, Clave> = {
    Sand: 'obra.suelo.arena',
    Clay: 'obra.suelo.arcilla',
    Rocky: 'obra.suelo.rocoso',
};

const CLIMA: Record<NonNullable<SiteConditions['weather']>, Clave> = {
    Cold: 'obra.clima.frio',
    Normal: 'obra.clima.normal',
    Hot: 'obra.clima.calor',
};

interface SiteConditionsFormProps {
    conditions: SiteConditions;
    onChange: (conditions: SiteConditions) => void;
}

const SiteConditionsForm: React.FC<SiteConditionsFormProps> = ({ conditions, onChange }) => {
    const { t } = useIdioma();

    // Ensure all properties have a safe fallback to prevent crashes on new estimates
    const safeConditions: SiteConditions = {
        accessWidthInches: conditions?.accessWidthInches ?? 48,
        soilType: conditions?.soilType ?? 'Sand',
        slopePercent: conditions?.slopePercent ?? 0,
        difficultyFactor: conditions?.difficultyFactor ?? 1.0,
        distanceToStreetFt: conditions?.distanceToStreetFt ?? 25,
        weather: conditions?.weather ?? 'Normal',
    };

    const update = (field: keyof SiteConditions, value: any) => {
        onChange({ ...safeConditions, [field]: value });
    };

    return (
        <div className="bg-white border border-acero-200 rounded-2xl p-6 space-y-6 animate-in fade-in slide-in-from-bottom-4">
            <div className="flex items-center gap-3 border-b border-acero-100 pb-4">
                <MapPin size={18} className="text-acero-500 shrink-0" />
                <div>
                    <h3 className="font-semibold tracking-tight text-navy-950 text-lg">{t('obra.titulo')}</h3>
                    <p className="text-xs text-acero-500">{t('obra.sub')}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Access Width */}
                <div className="space-y-2">
                    <label className="label-ui flex items-center gap-1">
                        <Ruler size={13} /> {t('obra.anchoAcceso')}
                    </label>
                    <div className="relative">
                        <input
                            type="number"
                            value={safeConditions.accessWidthInches}
                            onChange={(e) => update('accessWidthInches', Number(e.target.value))}
                            className={`w-full h-10 px-3 pr-16 border rounded-xl font-data tabular-nums text-navy-950 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:ring-2 ${safeConditions.accessWidthInches < 36 ? 'border-aviso bg-aviso-bg focus:border-aviso focus:ring-aviso/25' : 'border-acero-300 bg-white focus:border-navy-600 focus:ring-brand-400/55'}`}
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 label-ui pointer-events-none">{t('obra.pulgadas')}</span>
                    </div>
                    {safeConditions.accessWidthInches < 36 && (
                        <div className="flex items-start gap-2 text-aviso text-xs bg-aviso-bg p-2.5 rounded-xl">
                            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                            <span>{t('obra.avisoAccesoEstrecho')}</span>
                        </div>
                    )}
                </div>

                {/* Soil Type */}
                <div className="space-y-2">
                    <label className="label-ui flex items-center gap-1">
                        <Mountain size={13} /> {t('obra.tipoSuelo')}
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                        {(['Sand', 'Clay', 'Rocky'] as const).map(type => (
                            <button
                                key={type}
                                onClick={() => update('soilType', type)}
                                className={`h-10 px-3 rounded-xl text-[13px] border transition-colors duration-150 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${safeConditions.soilType === type
                                        ? 'bg-navy-850 text-white border-navy-850 font-semibold'
                                        : 'bg-white text-acero-600 border-acero-300 hover:border-acero-400 hover:text-navy-950 font-medium'
                                    }`}
                            >
                                {t(SUELO[type])}
                            </button>
                        ))}
                    </div>
                    {safeConditions.soilType === 'Clay' && (
                        <div className="text-navy-700 text-xs bg-navy-50 p-2.5 rounded-xl flex items-start gap-2">
                            <Info size={14} className="shrink-0 mt-0.5" />
                            <span>{t('obra.avisoArcilla')}</span>
                        </div>
                    )}
                </div>

                {/* Distance to Street */}
                <div className="space-y-2">
                    <label className="label-ui flex items-center gap-1">
                        <Truck size={13} /> {t('obra.distanciaAcarreo')}
                    </label>
                    <div className="relative">
                        <input
                            type="number"
                            value={safeConditions.distanceToStreetFt}
                            onChange={(e) => update('distanceToStreetFt', Number(e.target.value))}
                            className="w-full h-10 px-3 pr-14 bg-white border border-acero-300 rounded-xl font-data tabular-nums text-navy-950 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 label-ui pointer-events-none">{t('obra.pies')}</span>
                    </div>
                    {safeConditions.distanceToStreetFt > 100 && (
                        <div className="flex items-start gap-2 text-aviso text-xs bg-aviso-bg p-2.5 rounded-xl">
                            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                            <span>{t('obra.avisoBomba')}</span>
                        </div>
                    )}
                </div>

                {/* Slope */}
                <div className="space-y-2">
                    <label className="label-ui flex items-center gap-1">
                        <Mountain size={13} /> {t('obra.pendiente')}
                    </label>
                    <div className="relative">
                        <input
                            type="number"
                            value={safeConditions.slopePercent}
                            onChange={(e) => update('slopePercent', Number(e.target.value))}
                            className="w-full h-10 px-3 pr-10 bg-white border border-acero-300 rounded-xl font-data tabular-nums text-navy-950 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 label-ui pointer-events-none">%</span>
                    </div>
                    {safeConditions.slopePercent > 2 && (
                        <div className="flex items-start gap-2 text-navy-700 text-xs bg-navy-50 p-2.5 rounded-xl">
                            <Info size={14} className="shrink-0 mt-0.5" />
                            <span>{t('obra.avisoPendiente')}</span>
                        </div>
                    )}
                </div>

                {/* Weather */}
                <div className="md:col-span-2 space-y-2">
                    <label className="label-ui flex items-center gap-1">
                        <Thermometer size={13} /> {t('obra.clima')}
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                        {([
                            { key: 'Cold', icon: <Snowflake size={14} /> },
                            { key: 'Normal', icon: <Thermometer size={14} /> },
                            { key: 'Hot', icon: <Sun size={14} /> },
                        ] as const).map(({ key, icon }) => (
                            <button
                                key={key}
                                onClick={() => update('weather', key)}
                                className={`h-10 px-3 rounded-xl text-[13px] border transition-colors duration-150 flex items-center justify-center gap-1.5 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${safeConditions.weather === key
                                        ? 'bg-navy-850 text-white border-navy-850 font-semibold'
                                        : 'bg-white text-acero-600 border-acero-300 hover:border-acero-400 hover:text-navy-950 font-medium'
                                    }`}
                            >
                                {icon}{t(CLIMA[key])}
                            </button>
                        ))}
                    </div>
                    {safeConditions.weather === 'Hot' && (
                        <div className="flex items-start gap-2 text-aviso text-xs bg-aviso-bg p-2.5 rounded-xl">
                            <Sun size={14} className="shrink-0 mt-0.5" />
                            <span>{t('obra.avisoCalor')}</span>
                        </div>
                    )}
                    {safeConditions.weather === 'Cold' && (
                        <div className="flex items-start gap-2 text-navy-700 text-xs bg-navy-50 p-2.5 rounded-xl">
                            <Snowflake size={14} className="shrink-0 mt-0.5" />
                            <span>{t('obra.avisoFrio')}</span>
                        </div>
                    )}
                </div>

                {/* General Access Difficulty */}
                <div className="md:col-span-2 space-y-2 border-t border-acero-100 pt-4 mt-2">
                    <div className="flex justify-between">
                        <label className="label-ui">{t('obra.dificultadAcceso')}</label>
                        <span className="text-sm font-data tabular-nums font-semibold text-navy-950">{safeConditions.difficultyFactor.toFixed(2)}x</span>
                    </div>
                    <input
                        type="range"
                        min="1.0"
                        max="2.0"
                        step="0.05"
                        value={safeConditions.difficultyFactor}
                        onChange={(e) => update('difficultyFactor', Number(e.target.value))}
                        aria-label={t('obra.dificultadAcceso')}
                        className="w-full h-2 bg-acero-100 rounded-lg appearance-none cursor-pointer accent-navy-850 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                    />
                    <div className="flex justify-between label-ui">
                        <span>{t('obra.dificultad.estandar')} (<span className="font-data">1.0</span>)</span>
                        <span>{t('obra.dificultad.dificil')} (<span className="font-data">1.5</span>)</span>
                        <span>{t('obra.dificultad.extremo')} (<span className="font-data">2.0</span>)</span>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default SiteConditionsForm;
